import React, { useCallback, useEffect, useState } from 'react';
import {
    CheckButton,
    DropDownBoxComponentSpacing,
    ShowIf,
    StateDropDown,
    TextBox,
    ToggleButton,
    ToggleButtonItem,
    ToggleButtonLabelSize,
    YesNoToggleButton,
} from '@apply/components';
import { ButtonGroupTypes } from 'devextreme-react/button-group';
import { useBusinessProfileQuery, useSaveBusinessProfile } from '../queries/businessProfileQueries';
import { Address, BusinessProfile, TriState, emptyAddress } from '../types';
import { AddressFields } from '../components/AddressFields';
import { StepStatus } from '../components/StepStatus';
import { RegisterStep } from '../wizard/stepApi';
import { asInput } from '../components/inputValue';

interface BusinessProfileStepProps {
    onRegister: RegisterStep;
}

const emptyProfile = (): BusinessProfile => ({
    id: 0,
    name: '',
    website: '',
    mailingAddress: emptyAddress(),
    physicalAddressSameAsMailing: false,
    physicalAddress: emptyAddress(),
    license: {
        usesOwnLicense: null,
        licenseState: '',
        licenseNumber: '',
        hasBeenRevoked: null,
    },
    lastEditedUtc: null,
});

const LICENSE_ITEMS: ToggleButtonItem[] = [
    { id: 'Yes', text: 'Yes', hint: 'Click Yes' },
    { id: 'No', text: 'No', hint: 'Click No' },
    { id: 'NotApplicable', text: 'N/A', hint: 'Click N/A' },
];

const triStateToId = (value: TriState | null): string[] => {
    switch (value) {
        case TriState.No:
            return ['No'];
        case TriState.Yes:
            return ['Yes'];
        case TriState.NotApplicable:
            return ['NotApplicable'];
        default:
            return [];
    }
};

export const BusinessProfileStep: React.FC<BusinessProfileStepProps> = ({ onRegister }) => {
    const { data, isLoading, isError, error, refetch } = useBusinessProfileQuery();
    const { mutateAsync: saveProfile } = useSaveBusinessProfile();

    const [form, setForm] = useState<BusinessProfile>(emptyProfile);
    const [isDirty, setIsDirty] = useState(false);

    // Seed from the server, but never on top of edits the user has not saved yet
    useEffect(() => {
        if (data && !isDirty) {
            setForm(data);
        }
    }, [data, isDirty]);

    const save = useCallback(async () => {
        if (!isDirty) {
            return;
        }
        await saveProfile(form);
        setIsDirty(false);
    }, [form, isDirty, saveProfile]);

    useEffect(() => {
        onRegister({ save, isDirty });
    }, [save, isDirty, onRegister]);

    const update = <K extends keyof BusinessProfile>(key: K, value: BusinessProfile[K]) => {
        setIsDirty(true);
        setForm((prior) => ({ ...prior, [key]: value }));
    };

    const updateLicense = <K extends keyof BusinessProfile['license']>(
        key: K,
        value: BusinessProfile['license'][K],
    ) => {
        setIsDirty(true);
        setForm((prior) => ({ ...prior, license: { ...prior.license, [key]: value } }));
    };

    const updateAddress = (key: 'mailingAddress' | 'physicalAddress', patch: Partial<Address>) => {
        setIsDirty(true);
        setForm((prior) => ({ ...prior, [key]: { ...prior[key], ...patch } }));
    };

    const onLicenseUsageClick = (e: ButtonGroupTypes.ItemClickEvent) => {
        const id = (e.itemData as ToggleButtonItem)?.id;
        updateLicense('usesOwnLicense', TriState[id as keyof typeof TriState] ?? null);
    };

    if (isLoading || isError) {
        return (
            <StepStatus
                isLoading={isLoading}
                isError={isError}
                error={error}
                onRetry={() => void refetch()}
            />
        );
    }

    return (
        <div className='bp-step'>
            <TextBox
                id='bpCompanyName'
                value={asInput(form.name)}
                onChange={(value) => update('name', value)}
                label='General or Umbrella Company Name'
                required={true}
                maxLength={100}
            />

            <TextBox
                id='bpWebsite'
                value={asInput(form.website)}
                onChange={(value) => update('website', value)}
                label='Website'
                maxLength={500}
            />

            <div
                className='pageSubHeader'
                style={{ marginTop: 30 }}
            >
                Mailing Address
            </div>
            <AddressFields
                idPrefix='bpMailing'
                value={form.mailingAddress}
                requiredFields
                onChange={(patch: Partial<Address>) => updateAddress('mailingAddress', patch)}
            />

            <div style={{ marginTop: 20 }}>
                <CheckButton
                    id='bpPhysicalSameAsMailing'
                    label='Physical Address Same as Mailing Address'
                    selected={form.physicalAddressSameAsMailing}
                    onToggle={() =>
                        update('physicalAddressSameAsMailing', !form.physicalAddressSameAsMailing)
                    }
                />
            </div>

            <ShowIf showIf={!form.physicalAddressSameAsMailing}>
                <div
                    className='pageSubHeader'
                    style={{ marginTop: 30 }}
                >
                    Physical Address
                </div>
                <AddressFields
                    idPrefix='bpPhysical'
                    value={form.physicalAddress}
                    requiredFields
                    onChange={(patch: Partial<Address>) => updateAddress('physicalAddress', patch)}
                />
            </ShowIf>

            <div style={{ marginTop: 30 }}>
                <ToggleButton
                    id='bpUsesOwnLicense'
                    items={LICENSE_ITEMS}
                    selectedItems={triStateToId(form.license.usesOwnLicense)}
                    selectionMode={form.license.usesOwnLicense === null ? 'none' : 'single'}
                    stylingMode='outlined'
                    onItemClick={onLicenseUsageClick}
                    width='345px'
                    height='40px'
                    label='Will you be using your own license, or a third-party general contractor?'
                    labelSize={ToggleButtonLabelSize.Medium}
                />
            </div>

            <ShowIf showIf={form.license.usesOwnLicense === TriState.Yes}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ flex: '1 1 272px', minWidth: '272px' }}>
                        <StateDropDown
                            id='bpLicenseState'
                            value={asInput(form.license.licenseState)}
                            onChange={(value) => updateLicense('licenseState', value)}
                            label='License State'
                            componentSpacing={DropDownBoxComponentSpacing.SmallSpacing}
                        />
                    </div>
                    <div style={{ flex: '1 1 140px', minWidth: '140px' }}>
                        <TextBox
                            id='bpLicenseNumber'
                            value={asInput(form.license.licenseNumber)}
                            onChange={(value) => updateLicense('licenseNumber', value)}
                            label='License Number'
                            maxLength={50}
                        />
                    </div>
                </div>

                <div style={{ marginTop: 20 }}>
                    <YesNoToggleButton
                        id='bpLicenseRevoked'
                        value={form.license.hasBeenRevoked ?? undefined}
                        onItemClick={(e) =>
                            updateLicense('hasBeenRevoked', (e.itemData as ToggleButtonItem)?.id === 'Yes')
                        }
                        width='230px'
                        height='40px'
                        label='Has this license ever been revoked?'
                        labelSize={ToggleButtonLabelSize.Medium}
                    />
                </div>
            </ShowIf>
        </div>
    );
};
